// Кнопка полного экрана: третья в ряду левого нижнего угла, за таблицей выплат и правилами.
//
// Значок один на оба положения: художник дал четыре золотых уголка и ничего на возврат в окно.
// Кнопка поэтому только переключает, а в каком игра положении, видно по самому экрану.
//
// Кадр значка 190x190, вдвое крупнее соседей по ряду в 84 пикселя, поэтому он ужат до их
// размера. Ряд должен читаться рядом, а не кнопкой среди кнопок.
//
// Своё место в ряду кнопка считает от края макета сама, как и кнопка настроек в верхнем ряду.
// Все три кнопки ряда одного размера, поэтому третьей хватает своего радиуса и зазора.
//
// Полного экрана в браузере может не быть вовсе. Тогда кнопки на экране нет: нажимать её было бы
// не на что, см. ui/fullscreen.ts.

import { Container } from 'pixi.js';

import { UI_SHEET, type UiFrame } from '../assets/catalog';
import type { AssetManager, TextureSheet } from '../assets/manager';

import { RoundButton } from './button';
import { fullscreenAvailable, toggleFullscreen } from './fullscreen';
import { minTouchableScale, ROW_BUTTON_GAP, rowButtonGap, type GameLayout, viewportEdge } from './layout';
import { DESIGN_HEIGHT, DESIGN_WIDTH, EDGE_MARGIN } from './scene';

/** Размер соседних кнопок ряда в единицах макета. До него ужат кадр значка. */
const ROW_ICON_SIZE: number = 84;

/** Наименьший радиус кнопки в физических пикселях. Тот же, что у кнопок помощи. */
const TOUCH_RADIUS: number = 24;

/** Кнопка полного экрана. */
export class FullscreenButton {
  readonly view: Container = new Container();

  readonly #button: RoundButton;

  /** Во сколько раз кадр ужат до размера соседей по ряду. */
  readonly #icon: number;

  constructor(assets: AssetManager) {
    const ui: TextureSheet<UiFrame> = assets.sheet(UI_SHEET);

    this.#button = new RoundButton(
      { idle: ui.fullscreenIdle, hover: ui.fullscreenHover, active: ui.fullscreenActive },
      () => toggleFullscreen(),
    );

    this.#icon = ROW_ICON_SIZE / (this.#button.radius * 2);

    const radius: number = this.#button.radius * this.#icon;

    this.#button.view.scale.set(this.#icon);
    this.#button.view.position.set(
      -DESIGN_WIDTH / 2 + EDGE_MARGIN + radius * 5 + ROW_BUTTON_GAP * 2,
      DESIGN_HEIGHT / 2 - EDGE_MARGIN - radius,
    );

    // Без полного экрана кнопки на экране нет. Спрашивается это один раз: своё решение
    // браузер по ходу игры не меняет, и пропасть на глазах игрока кнопке не с чего.
    this.view.visible = fullscreenAvailable();
    this.view.addChild(this.#button.view);
  }

  /** Двигает затухание кнопки. */
  update(seconds: number): void {
    this.#button.update(seconds);
  }

  layout(layout: GameLayout): void {
    const { bottom, bounds, composition, viewport } = layout;
    const mobile: boolean = composition === 'mobile';
    const scale: number = mobile ? minTouchableScale(viewport, this.#button.radius, TOUCH_RADIUS, this.#icon) : this.#icon;
    const radius: number = this.#button.radius * scale;
    const edge: number = mobile ? viewportEdge(viewport) : EDGE_MARGIN;
    const gap: number = rowButtonGap(layout);

    this.#button.view.scale.set(scale);
    this.#button.view.position.set(
      bounds.left + edge + radius * 5 + gap * 2,
      mobile ? bottom.buttonBottom - radius : bounds.bottom - EDGE_MARGIN - radius,
    );
  }
}
